import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Tag, ChevronRight, Loader2, Coins } from 'lucide-react';
import { useAppState } from '../context/AppStateContext';
import { useAuth } from '../context/AuthContext';
import { NICHE_IMAGES } from '../utils/constants';
import { wixCommentsApi, groupsApi } from '../services/api';
import GroupDealsDrawer from '../components/groups/GroupDealsDrawer';
import GuestPromptDrawer from '../components/shared/GuestPromptDrawer';

// Format timestamp to readable date/time
const formatDateTime = (timestamp, dateStr) => {
  try {
    const date = timestamp ? new Date(timestamp) : dateStr ? new Date(dateStr) : null;
    if (!date || isNaN(date.getTime())) return 'Just now';

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return 'Just now';
  }
};

// Get random memoji avatar based on gender
const getGenderAvatar = (gender) => {
  const femaleMemojis = ['/avatars/Female-memoji-1.svg', '/avatars/Female-memoji-2.svg', '/avatars/Female-memoji-3.svg', '/avatars/Female-memoji-4.svg'];
  const maleMemojis = ['/avatars/Male-memoji-5.svg', '/avatars/Male-memoji-6.svg', '/avatars/Male-memoji-7.svg'];
  const avatarList = (gender || '').toLowerCase() === 'female' ? femaleMemojis : maleMemojis;
  return avatarList[Math.floor(Math.random() * avatarList.length)];
};

export default function GroupChatPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const {
    selectedGroup,
    setSelectedGroup,
    profileForm,
    showGroupDealsDrawer,
    setShowGroupDealsDrawer,
    groupChatRefreshTrigger,
  } = useAppState();
  const { isGuestMode, setShowGuestPrompt, user } = useAuth();
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupLoading, setGroupLoading] = useState(true);
  const [group, setGroup] = useState(selectedGroup);

  // Fetch group data if not already selected
  useEffect(() => {
    const fetchGroup = async () => {
      if (selectedGroup) {
        setGroup(selectedGroup);
        setGroupLoading(false);
        return;
      }

      if (!groupId) {
        navigate('/groups');
        return;
      }

      setGroupLoading(true);
      try {
        const groupData = await groupsApi.getOne(groupId);
        setGroup(groupData);
        setSelectedGroup(groupData);
      } catch (error) {
        console.error('Failed to fetch group:', error);
        navigate('/groups');
      } finally {
        setGroupLoading(false);
      }
    };

    fetchGroup();
  }, [groupId, selectedGroup, setSelectedGroup, navigate]);

  // Fetch comments from Wix API
  const fetchGroupComments = useCallback(async () => {
    if (!groupId) return;

    setLoading(true);
    try {
      const { comments } = await wixCommentsApi.getComments(groupId);

      const mappedMessages = comments.map(comment => ({
        id: comment.id,
        author: comment.author,
        avatar: comment.avatar && !comment.avatar.includes('dicebear')
          ? comment.avatar
          : getGenderAvatar(comment.gender),
        text: (comment.text || '').replace(/\s*\[test\]\s*$/i, ''),
        time: formatDateTime(comment.timestamp, comment.date),
        type: comment.author === 'bonndleagent' ? 'cashback_announcement' : 'message',
      }));

      setMessages(mappedMessages);
    } catch (error) {
      console.error('Failed to fetch group comments:', error);
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  // Fetch comments when group is selected
  useEffect(() => {
    if (groupId) {
      fetchGroupComments();
    }
  }, [groupId, fetchGroupComments]);

  // Refresh comments when trigger changes (after successful payment)
  useEffect(() => {
    if (groupChatRefreshTrigger && groupId) {
      fetchGroupComments();
    }
  }, [groupChatRefreshTrigger, groupId, fetchGroupComments]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    // Guest users need to sign up to send messages
    if (isGuestMode) {
      setShowGuestPrompt(true);
      return;
    }

    const messageText = newMessage.trim();
    setNewMessage('');

    const userGender = (profileForm?.gender || user?.gender || '').toLowerCase();
    const userAvatar = getGenderAvatar(userGender);

    // Optimistically add message to local state
    const optimisticMessage = {
      id: Date.now(),
      author: profileForm?.bonndleTag || user?.bold_tag || profileForm?.firstName || 'You',
      avatar: userAvatar,
      text: messageText,
      time: 'Just now',
      type: 'message',
    };
    setMessages(prev => [...prev, optimisticMessage]);

    // Post to Wix API
    if (groupId) {
      try {
        await wixCommentsApi.addComment(groupId, {
          author: profileForm?.bonndleTag || user?.bold_tag || profileForm?.firstName || 'User',
          avatar: userAvatar,
          text: messageText,
          userId: user?.uuid || user?.id || null,
        });
      } catch (error) {
        console.error('Failed to post message to Wix:', error);
      }
    }
  };

  const handleBack = () => {
    setSelectedGroup(null);
    navigate('/groups');
  };

  // Show loading while fetching group data
  if (groupLoading) {
    return (
      <div className="flex flex-col h-screen h-dvh bg-white">
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={32} className="animate-spin text-indigo-600" />
        </div>
      </div>
    );
  }

  if (!group) {
    return null;
  }

  // Use deals from the group data
  const deals = group.deals || [];
  const merchants = group.merchants || [];
  const activeDeals = deals.length > 0 ? deals : merchants;
  const memberCount = group.members?.length || group.member_count || 0;

  return (
    <div className="flex flex-col h-screen h-dvh bg-white">
      {/* Header - Fixed */}
      <header className="flex items-center gap-3 p-4 border-b border-gray-100 bg-white safe-area-top">
        <button
          onClick={handleBack}
          className="p-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={24} />
        </button>
        <img
          src={(group.name || '').toLowerCase().includes('bonndle')
            ? '/logo/bonndleLogoIcon_Indigo.svg'
            : group.image_url || group.avatar || NICHE_IMAGES[group.niche]?.[0] || NICHE_IMAGES.general[0]}
          alt={group.name}
          className="w-10 h-10 rounded-xl object-cover"
        />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 truncate">{group.name}</p>
          <p className="text-xs text-gray-500">
            {memberCount} members
            {activeDeals.length > 0 && ` • ${activeDeals.length} Active Deals`}
          </p>
        </div>
      </header>

      {/* Active Deals Banner */}
      {activeDeals.length > 0 && (
        <button
          onClick={() => setShowGroupDealsDrawer(true)}
          className="flex items-center gap-4 p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left"
        >
          <div className="flex -space-x-3">
            {activeDeals.slice(0, 4).map((deal, index) => (
              <img
                key={deal.merchant_name || deal.uuid || index}
                src={deal.merchant_logo}
                alt={deal.merchant_name}
                className="w-8 h-8 rounded-full border-2 border-white object-contain shadow-sm bg-white p-0.5"
                style={{ zIndex: 4 - index }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ))}
          </div>
          <span className="flex-1" />
          <span className="px-3 py-2 bg-indigo-600 text-white text-xs font-bold rounded-lg whitespace-nowrap flex items-center gap-1">
            View Deals
            <ChevronRight size={14} />
          </span>
        </button>
      )}

      {/* Messages - Scrollable */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="animate-spin text-indigo-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-gray-500">Start coordinating with your group!</p>
            <p className="text-xs text-gray-400 mt-1">Messages will appear here</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isBonndleAgent = msg.author === 'bonndleagent';
            return (
              <div key={msg.id} className="flex gap-3">
                {isBonndleAgent ? (
                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Coins size={16} className="text-white" />
                  </div>
                ) : (
                  <img
                    src={msg.avatar}
                    alt={msg.author}
                    className="w-8 h-8 rounded-full bg-gray-100 flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-semibold text-sm ${isBonndleAgent ? 'text-indigo-600' : 'text-gray-900'}`}>
                      {msg.author}
                    </span>
                    {msg.type === 'deal_comment' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-full">
                        <Tag size={10} />
                        {msg.dealBrand}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">{msg.time}</span>
                  </div>
                  <div className={`rounded-2xl rounded-tl-sm p-3 shadow-sm ${
                    isBonndleAgent ? 'bg-indigo-50 border border-indigo-100' : 'bg-white'
                  }`}>
                    <p className={`text-sm ${isBonndleAgent ? 'text-indigo-700' : 'text-gray-700'}`}>
                      {msg.text}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Input - Fixed at bottom */}
      <div className="p-4 border-t border-gray-100 bg-white safe-area-bottom">
        <div className="flex items-end gap-2">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            placeholder="Message group..."
            rows={1}
            className="flex-1 px-4 py-3 bg-gray-100 rounded-xl text-sm font-medium text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-indigo-500 resize-none min-h-[48px] max-h-[120px]"
            style={{ overflow: 'auto' }}
          />
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            <Send size={20} />
          </button>
        </div>
      </div>

      {/* Group Deals Drawer */}
      {showGroupDealsDrawer && <GroupDealsDrawer />}

      {/* Guest Prompt Drawer */}
      <GuestPromptDrawer />
    </div>
  );
}
